<template>
  <ion-modal :is-open="isOpen" @didDismiss="$emit('close')">
    <ion-header>
      <ion-toolbar>
        <ion-title>Select Chapter</ion-title>
        <ion-buttons slot="end">
          <ion-button @click="$emit('close')">Close</ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="chapters-list">
        <div
          v-for="(campaign, index) in campaigns"
          :key="campaign.id"
          :class="[
            'chapter-card',
            {
              'locked': !isChapterUnlocked(index),
              'current': isCurrentChapter(index),
              'completed': isChapterComplete(campaign),
            },
          ]"
          @click="onChapterClick(campaign, index)"
        >
          <div class="chapter-info">
            <h2>Chapter {{ index + 1 }}: {{ campaign.name }}</h2>
            <span class="progress-text">
              <template v-if="isChapterComplete(campaign)">
                Complete
              </template>
              <template v-else>
                {{ getCompletedCount(campaign) }}/{{ campaign.levelIds.length }} Battles Complete
              </template>
            </span>
            <div class="progress-bar">
              <div
                v-for="(levelId, i) in campaign.levelIds"
                :key="i"
                :class="['progress-segment', { filled: isLevelComplete(levelId) }]"
              />
            </div>
          </div>
          <div class="chapter-action">
            <ion-icon v-if="!isChapterUnlocked(index)" :icon="lockClosedOutline" class="action-icon lock-icon" />
            <ion-icon v-else-if="isChapterComplete(campaign)" :icon="checkmarkCircleOutline" class="action-icon complete-icon" />
            <ion-icon v-else :icon="chevronForwardOutline" class="action-icon play-icon" />
          </div>
          <div v-if="!isChapterUnlocked(index)" class="lock-text">
            Complete Chapter {{ index }} to unlock
          </div>
        </div>
      </div>
    </ion-content>
  </ion-modal>
</template>

<script setup>
import { computed, inject } from 'vue';
import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonIcon,
} from '@ionic/vue';
import { lockClosedOutline, checkmarkCircleOutline, chevronForwardOutline } from 'ionicons/icons';
import campaignsData from '../../content/dist/campaigns.json';

defineProps({
  isOpen: Boolean,
});

const emit = defineEmits(['close', 'select-chapter']);
const game = inject('game');
const campaigns = campaignsData.campaigns;

const isLevelComplete = (levelId) => !!game.state.progress.completedLevelIds[levelId];

const getCompletedCount = (campaign) => {
  return campaign.levelIds.filter(isLevelComplete).length;
};

const isChapterComplete = (campaign) => {
  return getCompletedCount(campaign) === campaign.levelIds.length;
};

/**
 * A chapter is unlocked if:
 * - It's the first chapter (index 0), OR
 * - The previous chapter's reward has been claimed, OR
 * - The previous chapter has no reward field AND all its battles are complete
 */
const isChapterUnlocked = (index) => {
  if (index === 0) return true;
  const prevCampaign = campaigns[index - 1];
  if (prevCampaign.reward) {
    return game.state.progress.claimedRewards[prevCampaign.id] === true;
  }
  // No reward field: treat as auto-claimed when all battles complete
  return isChapterComplete(prevCampaign);
};

/**
 * The "current" chapter is the first unlocked chapter that has incomplete battles.
 */
const currentChapterIndex = computed(() => {
  for (let i = 0; i < campaigns.length; i++) {
    if (isChapterUnlocked(i) && !isChapterComplete(campaigns[i])) {
      return i;
    }
  }
  return -1;
});

const isCurrentChapter = (index) => {
  return index === currentChapterIndex.value;
};

const onChapterClick = (campaign, index) => {
  if (!isChapterUnlocked(index)) return;
  emit('select-chapter', campaign);
};
</script>

<style scoped>
ion-content {
  --background: var(--hw-bg-base);
}

.chapters-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
}

.chapter-card {
  background: var(--hw-bg-surface);
  border: 1px solid var(--hw-border);
  border-radius: var(--hw-radius);
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.chapter-card:hover:not(.locked) {
  background: var(--hw-bg-elevated);
  border-color: var(--hw-border-strong);
  transform: translateY(-2px);
  box-shadow: var(--hw-shadow);
}

.chapter-card.current {
  border-color: var(--hw-accent);
  box-shadow: 0 0 8px var(--hw-accent-glow);
}

.chapter-card.locked {
  opacity: 0.5;
  cursor: not-allowed;
}

.chapter-info {
  flex: 1;
}

.chapter-info h2 {
  margin: 0 0 4px 0;
  font-size: 1.1em;
  font-weight: bold;
  color: var(--hw-text);
}

.progress-text {
  font-size: 0.85em;
  color: var(--hw-text-muted);
}

.chapter-card.completed .progress-text {
  color: var(--hw-success);
}

.chapter-action {
  position: absolute;
  top: 16px;
  right: 16px;
}

.action-icon {
  font-size: 22px;
}

.play-icon {
  color: var(--hw-text-muted);
}

.lock-icon {
  color: var(--hw-text-dim);
}

.complete-icon {
  color: var(--hw-success);
}

.lock-text {
  margin-top: 8px;
  font-size: 0.8em;
  color: var(--hw-text-dim);
  font-style: italic;
}

.progress-bar {
  display: flex;
  gap: 3px;
  margin-top: 8px;
}

.progress-segment {
  flex: 1;
  height: 4px;
  background: var(--hw-border);
  border-radius: var(--hw-radius);
}

.progress-segment.filled {
  background: var(--hw-accent);
}
</style>
